import { useState, useEffect, useCallback, useMemo } from "react";
import { getConfiguracionDocumentos } from "../../../services/ClienteService.js";
import { podarClientesPorBusqueda, dedupClientesPorCombinacion } from "../utils/agrupacionUtils.js";

const useConfiguracionDocumentos = (baseUrl, onError) => {
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState("");
  const [expandedRows, setExpandedRows] = useState(null);

  const searchConfiguracion = useCallback(
    async (mantenerExpansion = false) => {
      try {
        setLoading(true);
        const data = await getConfiguracionDocumentos(baseUrl, {});
        // Defensa contra combinaciones (cliente, empresa) repetidas en el
        // response: colapsa duplicados, fusiona asignaciones por id y deja
        // console.warn con la evidencia para rastrear la causa raíz
        setClientes(dedupClientesPorCombinacion(data.clientes));
        // Al recargar tras un guardado se preserva la expansión actual;
        // en la carga inicial se colapsa
        setExpandedRows(mantenerExpansion ? (prev) => prev : null);
      } catch (error) {
        console.error(error);
        onError?.(error);
      } finally {
        setLoading(false);
      }
    },
    [baseUrl, onError]
  );

  useEffect(() => {
    searchConfiguracion();
  }, [searchConfiguracion]);

  // Refresca el listado preservando la expansión actual (tras guardar)
  const recargar = useCallback(
    () => searchConfiguracion(true),
    [searchConfiguracion]
  );

  const clientesFiltrados = useMemo(
    () => podarClientesPorBusqueda(clientes, busqueda),
    [clientes, busqueda]
  );

  const toggleFila = useCallback((cliente) => {
    setExpandedRows((prev) => {
      const _expanded = { ...(prev ?? {}) };
      // Misma clave única por combinación (cliente, empresa) que el dataKey
      // de la tabla; con cliente.id dos empresas del mismo cliente compartían
      // expansión
      const clave = cliente.claveFila ?? cliente.id;
      if (_expanded[clave]) {
        delete _expanded[clave];
      } else {
        _expanded[clave] = true;
      }
      return _expanded;
    });
  }, []);

  return {
    clientesFiltrados,
    loading,
    busqueda,
    setBusqueda,
    expandedRows,
    setExpandedRows,
    toggleFila,
    recargar,
  };
};

export default useConfiguracionDocumentos;
